import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../i18n/hooks';
import { PageContainer, ArbiWinHeader, ArbiWinFooter, MainContainer, Button } from '../../ui';
import { CalculatorModal, CalculatorWrapper, openCalculatorFromSurebet } from '../../calculator';
import { SurebetService } from '../../surebets';
import NumberUtils from '../../utils/NumberUtils';
import './BridgePage.css';

/**
 * Maps coefficient types to neutral, stealth "position" labels.
 * Falls back to a generic positional index when a type is unknown.
 */
const POSITION_LABELS = {
  Coef1: '1',
  CoefX: 'X',
  Coef2: '2',
  Coef1X: '1X',
  CoefX2: 'X2',
  Coef12: '12'
};

const getPositionLabel = (type, index) => POSITION_LABELS[type] || String(index + 1);

const getSourceNumber = (index) => String(index + 1).padStart(2, '0');

/**
 * Returns the margin key with the highest value from a surebet item.
 * @param {Object} surebet - MarketsAnalysis item with a margins object
 * @returns {string|null} Best margin key or null
 */
const getBestMarginKey = (surebet) => {
  const margins = surebet?.margins || {};
  const keys = Object.keys(margins);
  if (keys.length === 0) return null;
  return keys.reduce((best, key) => {
    const current = margins[key]?.value ?? -Infinity;
    const bestValue = margins[best]?.value ?? -Infinity;
    return current > bestValue ? key : best;
  }, keys[0]);
};

/**
 * StealthMatrix - Renders a single live demo row from the masked endpoint
 * relabeled as a fintech "data discrepancy" without gambling terminology.
 */
const StealthMatrix = ({ onCalculator }) => {
  const { t } = useTranslation();
  const [demoItem, setDemoItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchDemo = async () => {
      setLoading(true);
      setError(false);
      try {
        const { data, error: fetchError } = await SurebetService.getMaskedMarketsAnalysis();
        if (!isMounted) return;
        if (fetchError || !data || !data.demoItem) {
          setError(Boolean(fetchError));
          setDemoItem(null);
        } else {
          setDemoItem(data.demoItem);
        }
      } catch (err) {
        if (isMounted) {
          setError(true);
          setDemoItem(null);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchDemo();

    return () => {
      isMounted = false;
    };
  }, []);

  const bestMarginKey = useMemo(() => getBestMarginKey(demoItem), [demoItem]);

  const marginData = bestMarginKey ? demoItem.margins[bestMarginKey] : null;

  const marketLabel = useMemo(() => {
    if (!bestMarginKey) return '';
    return t(`dataAnalytics.market.${bestMarginKey}`, {}, {
      defaultValue: t('dataAnalytics.market.default')
    });
  }, [bestMarginKey, t]);

  return (
    <div className="bridge-matrix">
      {loading && (
        <div className="bridge-matrix__state">
          <div className="bridge-matrix__spinner" />
          <p>{t('dataAnalytics.matrix.loading')}</p>
        </div>
      )}

      {!loading && (error || !marginData) && (
        <div className="bridge-matrix__state">
          <p>{error ? t('dataAnalytics.matrix.error') : t('dataAnalytics.matrix.empty')}</p>
        </div>
      )}

      {!loading && !error && marginData && (
        <div className="bridge-matrix__table-wrapper">
          <table className="bridge-matrix__table">
            <thead>
              <tr>
                <th>{t('dataAnalytics.matrix.eventColumn')}</th>
                <th>{t('dataAnalytics.matrix.marketColumn')}</th>
                <th>{t('dataAnalytics.matrix.nodesColumn')}</th>
                <th>{t('dataAnalytics.matrix.marginColumn')}</th>
                <th>{t('dataAnalytics.matrix.actionColumn')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="bridge-matrix__event">
                  {t('dataAnalytics.matrix.eventLabel', { id: demoItem.event?.id ?? '—' })}
                </td>
                <td className="bridge-matrix__market">{marketLabel}</td>
                <td>
                  <div className="bridge-matrix__nodes">
                    {(marginData.coefficients || []).map((coef, idx) => (
                      <div key={idx} className="bridge-matrix__node">
                        <span className="bridge-matrix__node-label">
                          {t('dataAnalytics.matrix.nodeLabel', {
                            n: getSourceNumber(idx),
                            pos: getPositionLabel(coef.type, idx)
                          })}:
                        </span>
                        <span className="bridge-matrix__node-value">
                          {NumberUtils.formatOdds(coef.value, { decimals: 2 })}
                        </span>
                      </div>
                    ))}
                  </div>
                </td>
                <td>
                  <span className="bridge-matrix__margin">
                    {NumberUtils.formatPercentage(marginData.value, { decimals: 2 })}
                  </span>
                </td>
                <td>
                  <button
                    type="button"
                    className="bridge-matrix__calc-btn"
                    onClick={() => onCalculator(demoItem, bestMarginKey)}
                  >
                    {t('dataAnalytics.matrix.calculator')}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

StealthMatrix.propTypes = {
  onCalculator: PropTypes.func.isRequired
};

/**
 * BridgePage - Hyper-Stealth Google Ads landing page (/data-analytics)
 *
 * Presents the platform as fintech-style market data analytics software while
 * keeping the same dark UI and green branding as the main site. Provides a
 * one-way funnel into the main landing page (/) and a live demo of a detected
 * "market discrepancy" sourced from the public masked endpoint.
 */
const BridgePage = () => {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const calculatorRef = useRef(null);

  const handleCalculator = useCallback((surebet, marginKey) => {
    openCalculatorFromSurebet(calculatorRef, surebet, marginKey);
  }, []);

  // Navigate to the main landing and scroll to the masked previews section.
  const goToPreviews = useCallback(() => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('landing-previews');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 300);
  }, [navigate]);

  // Hyper-Stealth SEO: this ad-only page must be noindex/nofollow and must
  // restore the global meta state on unmount (single-page application).
  useEffect(() => {
    const previousTitle = document.title;

    const robotsMeta = document.querySelector('meta[name="robots"]');
    const previousRobots = robotsMeta ? robotsMeta.getAttribute('content') : null;

    const descMeta = document.querySelector('meta[name="description"]');
    const previousDescription = descMeta ? descMeta.getAttribute('content') : null;

    document.title = t('dataAnalytics.seo.title');

    let robotsEl = robotsMeta;
    if (!robotsEl) {
      robotsEl = document.createElement('meta');
      robotsEl.setAttribute('name', 'robots');
      document.head.appendChild(robotsEl);
    }
    robotsEl.setAttribute('content', 'noindex, nofollow');

    let descEl = descMeta;
    if (!descEl) {
      descEl = document.createElement('meta');
      descEl.setAttribute('name', 'description');
      document.head.appendChild(descEl);
    }
    descEl.setAttribute('content', t('dataAnalytics.seo.description'));

    return () => {
      document.title = previousTitle;
      if (robotsEl) {
        robotsEl.setAttribute('content', previousRobots || 'index, follow');
      }
      if (descEl && previousDescription !== null) {
        descEl.setAttribute('content', previousDescription);
      }
    };
  }, [t, language]);

  return (
    <PageContainer>
      <CalculatorWrapper calculatorRef={calculatorRef} />
      <ArbiWinHeader
        isAuthenticated={false}
        onLogin={() => navigate('/login')}
        onRegister={() => navigate('/register')}
        onHomeClick={() => navigate('/')}
      />

      <MainContainer>
        {/* Section 1: Hero */}
        <section className="bridge-hero">
          <div className="bridge-hero__content">
            <h1 className="bridge-hero__title">{t('dataAnalytics.hero.title')}</h1>
            <p className="bridge-hero__subtitle">{t('dataAnalytics.hero.subtitle')}</p>
            <div className="bridge-hero__actions">
              <Button variant="primary" size="large" onClick={() => navigate('/')}>
                {t('dataAnalytics.hero.cta')}
              </Button>
            </div>
          </div>
        </section>

        {/* Section 2: Technology */}
        <section className="bridge-tech">
          <div className="bridge-tech__container">
            <h2 className="bridge-tech__title">{t('dataAnalytics.tech.title')}</h2>
            <p className="bridge-tech__text">{t('dataAnalytics.tech.text')}</p>
          </div>
        </section>

        {/* Section 3: Interactive Matrix (Stealth Live Demo) */}
        <section className="bridge-demo">
          <div className="bridge-demo__container">
            <h2 className="bridge-demo__title">{t('dataAnalytics.matrix.title')}</h2>
            <p className="bridge-demo__subtitle">{t('dataAnalytics.matrix.subtitle')}</p>
            <StealthMatrix onCalculator={handleCalculator} />
          </div>
        </section>

        {/* Section 4: Platform advantages */}
        <section className="bridge-features">
          <div className="bridge-features__container">
            <h2 className="bridge-features__title">{t('dataAnalytics.features.title')}</h2>
            <div className="bridge-features__grid">
              <div className="bridge-feature-card">
                <h3 className="bridge-feature-card__title">{t('dataAnalytics.features.card1.title')}</h3>
                <p className="bridge-feature-card__text">{t('dataAnalytics.features.card1.text')}</p>
              </div>
              <div className="bridge-feature-card">
                <h3 className="bridge-feature-card__title">{t('dataAnalytics.features.card2.title')}</h3>
                <p className="bridge-feature-card__text">{t('dataAnalytics.features.card2.text')}</p>
              </div>
              <div className="bridge-feature-card">
                <h3 className="bridge-feature-card__title">{t('dataAnalytics.features.card3.title')}</h3>
                <p className="bridge-feature-card__text">{t('dataAnalytics.features.card3.text')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 5: Bridge CTA */}
        <section className="bridge-cta">
          <div className="bridge-cta__container">
            <h2 className="bridge-cta__title">{t('dataAnalytics.bridge.title')}</h2>
            <p className="bridge-cta__text">{t('dataAnalytics.bridge.text')}</p>
            <button type="button" className="bridge-cta__button" onClick={goToPreviews}>
              {t('dataAnalytics.bridge.cta')}
            </button>
          </div>
        </section>
      </MainContainer>

      <ArbiWinFooter />
      <CalculatorModal />
    </PageContainer>
  );
};

export default BridgePage;
